import React from 'react';
import type { IntlShape } from 'react-intl';

import type { RoleCondition } from '../../../../services';

import { parseMarkersToJSX } from '../../../../shared/utils';

// Unicode characters used as invisible markers for bold formatting
// These are zero-width characters that won't display if accidentally rendered
const BOLD_START = '\u200B'; // Zero-width space - marks text for bold styling
const BOLD_END = '\u200C'; // Zero-width non-joiner - closes bold marker

/**
 * Generates a human-readable, ICU-compliant role description from conditions
 *
 * METHODOLOGY & INDUSTRY BEST PRACTICES:
 * =======================================
 * This implementation follows modern i18n best practices by:
 *
 * 1. Using Intl.ListFormat (ECMA-402 standard) for list formatting
 *    - Automatically handles grammar rules for all locales
 *    - Maintained by Unicode CLDR (Common Locale Data Repository)
 *
 * 2. Keeping translatable strings complete and unbroken
 *    - Translators see full sentence context
 *    - No programmatic string concatenation
 *    - Follows react-intl and ICU message format conventions
 *
 * 3. Separating structure from content
 *    - Only 2 message templates used (vs 155+ for all combinations)
 *    - Dynamic values injected into static templates
 *    - Browser handles locale-specific formatting
 *
 * 4. Optional rich text formatting via marker-based post-processing
 *    - Uses invisible Unicode markers to preserve ICU message integrity
 *    - Post-processes to JSX only when formatting is requested
 *    - Individual words bolded (not commas or connectors)
 *
 * WHAT GETS TRANSLATED:
 * =====================
 * - Sentence templates (2 used): wrapper, condition
 * - List formatting (automatic via Intl.ListFormat, no translation needed)
 *
 * WHAT DOES NOT GET TRANSLATED:
 * ==============================
 * - Attribute names from backend (e.g., "city", "department", "customproperty25")
 * - Attribute values from backend (e.g., "Chennai", "Manager", "FIN-001")
 * These are system identifiers and user data that remain consistent across locales
 *
 * EXAMPLES OF STRINGS THIS COVERS:
 * =================================
 * Single condition, single value:
 *   "This role is for users whose city is Chennai."
 *
 * Single condition, two values:
 *   "This role is for users whose city is Chennai or Bangalore."
 *
 * Single condition, three+ values (with Oxford comma):
 *   "This role is for users whose city is Chennai, Bangalore, or Mumbai."
 *
 * Two conditions:
 *   "This role is for users whose city is Chennai or Bangalore and role is Manager."
 *
 * Three+ conditions (Oxford comma added by Intl.ListFormat):
 *   "This role is for users whose city is Chennai, role is Manager, and department is HR."
 *
 * Complex example (3 conditions, mixed value counts):
 *   "This role is for users whose city is Chennai, Bangalore, or Mumbai,
 *    department is HR or Finance, and role is Manager."
 *
 * With bold formatting enabled:
 *   "This role is for users whose <strong>city</strong> is <strong>Chennai</strong> or <strong>Bangalore</strong>."
 *
 * @param conditions - Array of role conditions from API
 * @param l - Localization function from useInvertedLocalize
 * @param intl - Intl instance from useIntl (for locale-aware formatting)
 * @param options - Configuration options for formatting
 * @returns Human-readable, localized description (string or ReactNode with formatting)
 */
export default function generateRoleDescription(
  conditions: RoleCondition[],
  l: (defaultMessage: string, placeholders?: object) => string,
  intl: IntlShape,
  options: { boldAttributesAndValues?: boolean; locale?: string } = {}
): string | React.ReactNode {
  const { boldAttributesAndValues = false, locale } = options;

  if (conditions.length === 0) {
    return '';
  }

  const effectiveLocale = locale ?? intl.locale;

  // create list formatter for "or" lists (disjunction)
  // i.e., English: "A, B, or C" vs Spanish: "A, B o C" (no comma before "o")
  const disjunctionFormatter = new Intl.ListFormat(effectiveLocale, {
    type: 'disjunction', // "or" connector between items as needed
  });

  // create list formatter for "and" lists (conjunction)
  // used to combine multiple condition parts with locale-appropriate grammar
  // i.e., English: "A, B, and C" vs other locales with different rules
  const conjunctionFormatter = new Intl.ListFormat(effectiveLocale, {
    type: 'conjunction', // "and" connector between items as needed
  });

  // build each condition part (i.e., "city is Chennai or Bangalore")
  const conditionParts = conditions.map((condition) => {
    // normalize values to array
    const values = Array.isArray(condition.objectValue)
      ? condition.objectValue
      : [condition.objectValue];

    // if bold formatting requested, wrap individual values with invisible markers
    // these markers are preserved through ICU formatting and converted to JSX later
    const markedValues = boldAttributesAndValues
      ? values.map((v) => `${BOLD_START}${v}${BOLD_END}`)
      : values;

    // Intl.ListFormat automatically handles: commas, "or" placement, punctuation rules
    const formattedValues = disjunctionFormatter.format(markedValues);

    const attr = boldAttributesAndValues // wrap attribute name with markers if bold formatting
      ? `${BOLD_START}${condition.objectName}${BOLD_END}`
      : condition.objectName;

    // use localized template with dynamic attribute name and formatted values
    // template: "{attribute} is {values}" → "city is Chennai or Bangalore"
    return l('{attribute} is {values}', {
      attribute: attr,
      values: formattedValues,
    });
  });

  // use Intl.ListFormat to combine condition parts with locale-appropriate "and" grammar
  // i.e., English: "A and B" or "A, B, and C" (Oxford comma)
  const description = conjunctionFormatter.format(conditionParts);

  const fullDescription = l('This role is for users whose {description}.', {
    description,
  });

  // post-process: convert invisible markers to JSX <strong> tags
  if (boldAttributesAndValues) {
    return parseMarkersToJSX(fullDescription, [
      {
        start: BOLD_START,
        end: BOLD_END,
        createElement: (content, index) =>
          React.createElement('strong', { key: index }, content),
      },
    ]);
  }

  // if no bold formatting requested, return plain string (original behavior)
  return fullDescription;
}
